"use client";

import { Suspense, useEffect, useRef } from "react";
import Lottie from "lottie-react";
import BadgeIcon from "./BadgeIcon";

const badges = [
  {
    label: "Creative Analysis",
    color: "#5DA832",
    icon: (
      <path
        d="M4.53516 4.53516V12.2102H12.2102M6.24071 9.22543L7.94627 7.51988L9.65182 9.22543L11.7838 7.09349"
        stroke="white"
        strokeWidth="1.06597"
      />
    ),
  },
  {
    label: "AI Copywriter",
    color: "#7C5CFF",
    icon: (
      <path
        d="M4.96143 5.81445H11.7836M4.96143 8.37279H9.22531M4.96143 10.9311H7.94615"
        stroke="white"
        strokeWidth="1.06597"
      />
    ),
  },
  {
    label: "Image & Video AI",
    color: "#FF8A3D",
    icon: (
      <>
        <path
          d="M11.3574 4.53516H5.38793C4.91696 4.53516 4.53516 4.91696 4.53516 5.38793V11.3574C4.53516 11.8284 4.91696 12.2102 5.38793 12.2102H11.3574C11.8284 12.2102 12.2102 11.8284 12.2102 11.3574V5.38793C12.2102 4.91696 11.8284 4.53516 11.3574 4.53516Z"
          stroke="white"
          strokeWidth="1.06597"
        />
        <path
          d="M7.0935 7.94579C7.56448 7.94579 7.94628 7.56399 7.94628 7.09301C7.94628 6.62204 7.56448 6.24023 7.0935 6.24023C6.62252 6.24023 6.24072 6.62204 6.24072 7.09301C6.24072 7.56399 6.62252 7.94579 7.0935 7.94579Z"
          stroke="white"
          strokeWidth="1.06597"
        />
        <path
          d="M12.2099 9.65148L10.078 7.51953L5.3877 12.2098"
          stroke="white"
          strokeWidth="1.06597"
        />
      </>
    ),
    clipPath: (
      <defs>
        <clipPath id="clip0_2012_2652">
          <rect
            width="10.2333"
            height="10.2333"
            fill="white"
            transform="translate(3.25586 3.25586)"
          />
        </clipPath>
      </defs>
    ),
    wrapInClip: true,
  },
];

const FeatureCard = ({ card }) => {
  const badge = badges[card.badgeIndex];
  const articleRef = useRef(null);
  const lottieRef = useRef(null);

  useEffect(() => {
    const el = articleRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        lottieRef.current?.goToAndPlay(0, true);
        observer.unobserve(entry.target);
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <article
      ref={articleRef}
      className="group relative max-w-[400px] h-full bg-white border border-[#E8E0E0] rounded-[20px] lg:rounded-[30px] px-[26px] pt-[30px] flex flex-col gap-5 overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-[#3232A4]/8 hover:-translate-y-0.5 hover:border-[#C8B8B8]"
    >
      {/* Subtle top-edge accent that appears on hover */}
      <div
        className="absolute inset-x-0 top-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: `linear-gradient(90deg, transparent, ${badge.color}60, transparent)`,
        }}
        aria-hidden="true"
      />

      <header className="flex flex-col gap-1">
        {/* Badge */}
        <span className="flex items-center  mb-4 gap-1.5 w-fit px-3 py-1.5  text-[11px]  lg:text-[12px]  tracking-wide rounded-full font-semibold bg-[#F6F5F5] text-[#4A4558]">
          <BadgeIcon
            color={badge.color}
            icon={badge.icon}
            clipPath={badge.clipPath}
            wrapInClip={badge.wrapInClip}
          />
          {badge.label}
        </span>

        <h3 className="font-medium text-[#0E0B1A] max-w-[300px] tracking-normal lg:text-[20px] lg:leading-[26px] ">
          {card.title}
        </h3>

        <p className="text-[#0E0B1A]/60 font-normal text-[14px] leading-[1.6]">
          {card.description}
        </p>
      </header>

      {/* Animation */}
      <figure className="w-full mt-auto">
        <Suspense
          fallback={
            <div className="h-48 flex items-center justify-center text-xs text-[#9B97A8]">
              Loading…
            </div>
          }
        >
          <Lottie
            lottieRef={lottieRef}
            animationData={card.animation}
            loop={false}
            autoplay={false}
          />
        </Suspense>
        <figcaption className="sr-only">{card.caption}</figcaption>
      </figure>
    </article>
  );
};

export default FeatureCard;
